import type { ComponentProps } from 'react';
import { cn } from '@/lib/utils.js';

/*
 * A rule-lined panel, not a floating card: one hairline border, no drop
 * shadow, no rounded-everything. The design direction earns hierarchy from
 * scale contrast and rule-lines, so `Card` exists to bound a region — use it
 * sparingly and never as a uniform grid of equals.
 */
export function Card({ className, ...props }: ComponentProps<'section'>) {
  return (
    <section
      data-slot="card"
      className={cn(
        'flex flex-col gap-(--space-4) rounded-(--radius-md) border border-rule bg-card text-foreground',
        className,
      )}
      {...props}
    />
  );
}

export function CardHeader({ className, ...props }: ComponentProps<'header'>) {
  return (
    <header
      data-slot="card-header"
      className={cn(
        'flex flex-col gap-(--space-1) border-b border-rule px-(--space-5) py-(--space-3)',
        className,
      )}
      {...props}
    />
  );
}

export function CardTitle({ className, ...props }: ComponentProps<'h2'>) {
  return (
    <h2
      data-slot="card-title"
      className={cn('text-[length:var(--text-lead)] font-semibold', className)}
      {...props}
    />
  );
}

export function CardDescription({ className, ...props }: ComponentProps<'p'>) {
  return (
    <p
      data-slot="card-description"
      className={cn('text-[length:var(--text-small)] text-ink-muted', className)}
      {...props}
    />
  );
}

export function CardAction({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-action"
      className={cn('flex items-center gap-(--space-2)', className)}
      {...props}
    />
  );
}

export function CardContent({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div data-slot="card-content" className={cn('px-(--space-5) pb-(--space-4)', className)} {...props} />
  );
}

export function CardFooter({ className, ...props }: ComponentProps<'footer'>) {
  return (
    <footer
      data-slot="card-footer"
      className={cn(
        'flex items-center gap-(--space-2) border-t border-rule px-(--space-5) py-(--space-3)',
        className,
      )}
      {...props}
    />
  );
}
